import { useState } from 'react'
import {
  Brain,
  Target,
  AlertTriangle,
  Lightbulb,
  DollarSign,
  Sparkles,
  Pencil,
  Trash2,
  Check,
  X,
} from 'lucide-react'
import { useToast } from '@/components/Toast'
import {
  useProfile,
  useUpdateProfile,
  type ProfilePatch,
  type ProfileMemoryItem,
} from '@/lib/hooks'
import { cn } from '@/lib/cn'

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="rounded-xl border border-jarvis-border bg-jarvis-surface p-5">
      <div className="flex items-center gap-2 mb-3 text-jarvis-text">
        <span className="text-jarvis-blue">{icon}</span>
        <h2 className="text-sm font-semibold tracking-wide">{title}</h2>
      </div>
      {children}
    </section>
  )
}

function MemoryRow({
  item,
  onEdit,
  onDelete,
}: {
  item: ProfileMemoryItem
  onEdit: (text: string) => void
  onDelete: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(item.text)

  if (editing) {
    return (
      <li className="flex items-center gap-2 py-1.5">
        <input
          className="flex-1 bg-jarvis-surface-2 border border-jarvis-border rounded px-2 py-1 text-sm text-jarvis-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <button
          className="text-jarvis-blue"
          title="Save"
          onClick={() => {
            if (text.trim()) onEdit(text.trim())
            setEditing(false)
          }}
        >
          <Check size={16} />
        </button>
        <button
          className="text-jarvis-muted"
          title="Cancel"
          onClick={() => {
            setText(item.text)
            setEditing(false)
          }}
        >
          <X size={16} />
        </button>
      </li>
    )
  }

  return (
    <li className="group flex items-center gap-2 py-1.5">
      <span className="flex-1 text-sm text-jarvis-text">{item.text}</span>
      {item.confidence === 'high' ? (
        <span className="text-[10px] uppercase tracking-wide text-jarvis-blue">you told me</span>
      ) : null}
      <button
        className="opacity-0 group-hover:opacity-100 text-jarvis-muted hover:text-jarvis-text"
        title="Edit"
        onClick={() => setEditing(true)}
      >
        <Pencil size={14} />
      </button>
      <button
        className="opacity-0 group-hover:opacity-100 text-jarvis-muted hover:text-jarvis-red"
        title="Delete"
        onClick={onDelete}
      >
        <Trash2 size={14} />
      </button>
    </li>
  )
}

export function Profile() {
  const { data: profile, isLoading } = useProfile()
  const update = useUpdateProfile()
  const toast = useToast()
  const [prefs, setPrefs] = useState<string | null>(null)
  const [style, setStyle] = useState<string | null>(null)

  const patch = (body: ProfilePatch, msg?: string) =>
    update.mutate(body, {
      onSuccess: () => msg && toast.success(msg),
      onError: (e) => toast.error('Update failed', (e as Error).message),
    })

  if (isLoading) return <div className="text-jarvis-muted text-sm">Loading…</div>
  if (!profile) return <div className="text-jarvis-muted text-sm">No profile yet.</div>

  const lists: Array<{
    key: 'goals' | 'recurringConcerns' | 'facts'
    title: string
    icon: React.ReactNode
  }> = [
    { key: 'goals', title: 'Goals', icon: <Target size={16} /> },
    { key: 'recurringConcerns', title: 'Recurring concerns', icon: <AlertTriangle size={16} /> },
    { key: 'facts', title: 'What I know', icon: <Lightbulb size={16} /> },
  ]

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-jarvis-text flex items-center gap-2">
          <Brain size={20} className="text-jarvis-blue" /> What Jarvis knows about you
        </h1>
        <p className="text-sm text-jarvis-muted mt-1">
          Learned from your calls and chats. Edit or delete anything — it's your memory.
          {profile.pendingCount > 0
            ? ` ${profile.pendingCount} new insight${profile.pendingCount > 1 ? 's' : ''} will be organized tonight.`
            : ''}
        </p>
      </div>

      {profile.moneyMoves.length > 0 && (
        <Section icon={<DollarSign size={16} />} title="Money moves">
          <ul className="space-y-2">
            {profile.moneyMoves.map((mv) => (
              <li
                key={mv.id}
                className="flex items-start gap-3 rounded-lg border border-jarvis-border/60 p-3"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-jarvis-text">
                    {mv.title}
                    {mv.estimatedValue ? (
                      <span className="text-jarvis-blue ml-2">{mv.estimatedValue}</span>
                    ) : null}
                  </div>
                  {mv.rationale ? (
                    <div className="text-xs text-jarvis-muted mt-0.5">{mv.rationale}</div>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    className={cn(
                      'px-2 py-1 rounded text-xs',
                      mv.status === 'accepted'
                        ? 'bg-jarvis-blue/15 text-jarvis-blue'
                        : 'text-jarvis-muted hover:text-jarvis-text',
                    )}
                    onClick={() => patch({ moneyMove: { id: mv.id, status: 'accepted' } }, 'Accepted')}
                  >
                    {mv.status === 'accepted' ? 'Accepted' : 'Accept'}
                  </button>
                  <button
                    className="px-2 py-1 rounded text-xs text-jarvis-muted hover:text-jarvis-red"
                    onClick={() => patch({ moneyMove: { id: mv.id, status: 'dismissed' } })}
                  >
                    Dismiss
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {lists.map(({ key, title, icon }) => {
          const items = profile[key]
          return (
            <Section key={key} icon={icon} title={title}>
              {items.length === 0 ? (
                <p className="text-xs text-jarvis-muted">Nothing yet.</p>
              ) : (
                <ul>
                  {items.map((it) => (
                    <MemoryRow
                      key={it.id}
                      item={it}
                      onEdit={(text) => patch({ editItem: { id: it.id, text } }, 'Updated')}
                      onDelete={() => patch({ archiveItemId: it.id }, 'Removed')}
                    />
                  ))}
                </ul>
              )}
            </Section>
          )
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section icon={<Sparkles size={16} />} title="How you like info delivered">
          <textarea
            className="w-full bg-jarvis-surface-2 border border-jarvis-border rounded px-3 py-2 text-sm text-jarvis-text min-h-[72px]"
            defaultValue={profile.deliveryPreferences}
            placeholder="e.g. numbers first, then the one risk worth flagging"
            onChange={(e) => setPrefs(e.target.value)}
          />
          <button
            className="mt-2 px-3 py-1.5 rounded bg-jarvis-blue/15 text-jarvis-blue text-xs disabled:opacity-40"
            disabled={prefs === null}
            onClick={() => patch({ deliveryPreferences: prefs ?? '' }, 'Saved')}
          >
            Save
          </button>
        </Section>
        <Section icon={<Sparkles size={16} />} title="Your communication style">
          <textarea
            className="w-full bg-jarvis-surface-2 border border-jarvis-border rounded px-3 py-2 text-sm text-jarvis-text min-h-[72px]"
            defaultValue={profile.commsStyle}
            placeholder="e.g. direct, casual, no fluff"
            onChange={(e) => setStyle(e.target.value)}
          />
          <button
            className="mt-2 px-3 py-1.5 rounded bg-jarvis-blue/15 text-jarvis-blue text-xs disabled:opacity-40"
            disabled={style === null}
            onClick={() => patch({ commsStyle: style ?? '' }, 'Saved')}
          >
            Save
          </button>
        </Section>
      </div>
    </div>
  )
}
